import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Settings, Save, Loader2, Check, AlertCircle, BarChart3, Users, Mail, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [success, setSuccess] = useState({});
  const [error, setError] = useState(null);
  
  const [settings, setSettings] = useState({
    // CRM Settings
    pipedrive: {
      apiKey: '',
      pipelineId: '',
      configured: false
    },
    // Analytics Settings
    analytics: {
      ga4MeasurementId: '',
      metaPixelId: ''
    },
    // Digest Settings
    digest: {
      recipients: '',
      weekday: '0',
      monthday: '1',
      hour: '8'
    }
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data.settings || settings);
    } catch (err) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (section) => {
    setSaving({ ...saving, [section]: true });
    setSuccess({ ...success, [section]: false });
    setError(null);
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/${section}`,
        settings[section],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess({ ...success, [section]: true });
      setTimeout(() => setSuccess({ ...success, [section]: false }), 3000);
    } catch (err) {
      setError(`Failed to save ${section} settings`);
    } finally {
      setSaving({ ...saving, [section]: false });
    }
  };

  const updateSetting = (section, field, value) => {
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [field]: value
      }
    });
  };

  const weekdays = [
    { value: '0', label: 'Monday' },
    { value: '1', label: 'Tuesday' },
    { value: '2', label: 'Wednesday' },
    { value: '3', label: 'Thursday' },
    { value: '4', label: 'Friday' },
    { value: '5', label: 'Saturday' },
    { value: '6', label: 'Sunday' }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="w-6 h-6 mr-2" />
            Platform Settings
          </h1>
          <p className="text-gray-600">Configure integrations and system preferences</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        )}

        {/* CRM Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              CRM Integration (Pipedrive)
            </CardTitle>
            <CardDescription>
              Connect to Pipedrive to sync leads from accommodation forms and report downloads
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant={settings.pipedrive?.configured ? 'default' : 'secondary'}>
                {settings.pipedrive?.configured ? 'Connected' : 'Not Configured'}
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pipedriveApiKey">API Key</Label>
                <Input
                  id="pipedriveApiKey"
                  type="password"
                  value={settings.pipedrive?.apiKey || ''}
                  onChange={(e) => updateSetting('pipedrive', 'apiKey', e.target.value)}
                  placeholder="Enter Pipedrive API key"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find this in Pipedrive → Settings → Personal preferences → API
                </p>
              </div>
              <div>
                <Label htmlFor="pipelinePipelineId">Pipeline ID</Label>
                <Input
                  id="pipelinePipelineId"
                  value={settings.pipedrive?.pipelineId || ''}
                  onChange={(e) => updateSetting('pipedrive', 'pipelineId', e.target.value)}
                  placeholder="e.g., 1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The ID of the pipeline where leads should be created
                </p>
              </div>
            </div>
            
            <Button onClick={() => saveSettings('pipedrive')} disabled={saving.pipedrive}>
              {saving.pipedrive ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : success.pipedrive ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {success.pipedrive ? 'Saved!' : 'Save CRM Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Analytics Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
              Analytics & Tracking
            </CardTitle>
            <CardDescription>
              Configure Google Analytics 4 and Meta Pixel for visitor tracking and retargeting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ga4Id">Google Analytics 4 Measurement ID</Label>
                <Input
                  id="ga4Id"
                  value={settings.analytics?.ga4MeasurementId || ''}
                  onChange={(e) => updateSetting('analytics', 'ga4MeasurementId', e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find this in GA4 → Admin → Data Streams → Your stream
                </p>
              </div>
              <div>
                <Label htmlFor="metaPixelId">Meta Pixel ID</Label>
                <Input
                  id="metaPixelId"
                  value={settings.analytics?.metaPixelId || ''}
                  onChange={(e) => updateSetting('analytics', 'metaPixelId', e.target.value)}
                  placeholder="1234567890"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find this in Meta Events Manager → Data Sources
                </p>
              </div>
            </div>
            
            <Button onClick={() => saveSettings('analytics')} disabled={saving.analytics}>
              {saving.analytics ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : success.analytics ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {success.analytics ? 'Saved!' : 'Save Analytics Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Email Digest Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="w-5 h-5 mr-2 text-purple-600" />
              Email Digest Configuration
            </CardTitle>
            <CardDescription>
              Configure weekly and monthly digest email schedule and recipients
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="digestRecipients">Digest Recipients</Label>
              <Input
                id="digestRecipients"
                value={settings.digest?.recipients || ''}
                onChange={(e) => updateSetting('digest', 'recipients', e.target.value)}
                placeholder="email1@example.com, email2@example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated list of email addresses to receive digest reports
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Weekly Digest Day</Label>
                <Select
                  value={settings.digest?.weekday || '0'}
                  onValueChange={(value) => updateSetting('digest', 'weekday', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {weekdays.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Monthly Digest Day</Label>
                <Select
                  value={settings.digest?.monthday || '1'}
                  onValueChange={(value) => updateSetting('digest', 'monthday', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        Day {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Send Time (Hour)</Label>
                <Select
                  value={settings.digest?.hour || '8'}
                  onValueChange={(value) => updateSetting('digest', 'hour', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(24)].map((_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {i.toString().padStart(2, '0')}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>
                Weekly digest: Every {weekdays.find(d => d.value === settings.digest?.weekday)?.label || 'Monday'} at {settings.digest?.hour || '8'}:00
              </span>
            </div>
            
            <Button onClick={() => saveSettings('digest')} disabled={saving.digest}>
              {saving.digest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : success.digest ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {success.digest ? 'Saved!' : 'Save Digest Settings'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
